import { createAsyncThunk } from '@reduxjs/toolkit'
import { ProductDetailsServerData, ProductList } from '@lk/api'
import { translate } from 'loankeeper-common/helpers'

import { ThunkApi } from '../index.d'
import { actions as LKModalActions } from '../../features/shared/LKModal'
import { isLoaded } from '../shared/withLoadingState'
import { getProductList, getProductDetailsById } from './product.selectors'

interface LoadProductDetailsAttributes {
  productId: number
  productHash: string
}

interface LoadProductDetailsPayload {
  id: number
  details: ProductDetailsServerData
}

export const loadProductDetails = createAsyncThunk<LoadProductDetailsPayload, LoadProductDetailsAttributes, ThunkApi>(
  '[PRODUCT] LOAD_PRODUCT_DETAILS',
  async ({ productId, productHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.product.getDetails(productId, productHash)

    if (result.success) {
      return {
        id: productId,
        details: result.data,
      }
    }

    return rejectWithValue('Failed to load product details')
  },
)

export const loadProductDetailsIfNeeded = ({ productId, productHash }) => {
  return async (dispatch, getState) => {
    const state = getState()

    const details = getProductDetailsById(state, productId)

    if (details) {
      return details
    }

    const result = await dispatch(loadProductDetails({ productId, productHash }))

    if (loadProductDetails.fulfilled.match(result)) {
      return result.payload.details
    }

    return null
  }
}

export const loadProductList = createAsyncThunk<ProductList, void, ThunkApi>(
  '[PRODUCT] LOAD_PRODUCT_LIST',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.product.getList()

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate('frm_web_messages-PRODUCTS_MISSING')))

    return rejectWithValue('Failed to load product list')
  },
)

export const loadProductListIfNeeded = () => {
  return async (dispatch, getState) => {
    const state = getState()

    const slice = getProductList(state)

    if (isLoaded(slice)) {
      return slice.data
    }

    const result = await dispatch(loadProductList())

    if (loadProductList.fulfilled.match(result)) {
      return result.payload
    }

    return null
  }
}

export const loadProductBIC = createAsyncThunk<any, void, ThunkApi>(
  '[PRODUCT] LOAD_PRODUCT_BIC',
  async (_, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.product.getBIC()

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(result.error.message))

    return rejectWithValue('Failed to load product BIC')
  },
)

export const loadProductPawnshop = createAsyncThunk<any, void, ThunkApi>(
  '[PRODUCT] LOAD_PRODUCT_PAWNSHOP',
  async (_, { getState, extra, rejectWithValue }) => {
    const result = await extra.api.product.getPawnshop()

    if (result.success) {
      return result.data
    }

    return rejectWithValue('Failed to load product pawnshop')
  },
)
